import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Gavel, KeyRound, Hash, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { safeStorage } from "../lib/safeStorage";

export default function JudgeLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [eventCode, setEventCode] = useState(searchParams.get("event") || "");
  const [judgeCode, setJudgeCode] = useState(searchParams.get("judge") || "");
  const [loading, setLoading] = useState(false);
  const [festivalInfo, setFestivalInfo] = useState(null);

  const handleEventCodeBlur = async () => {
    if (eventCode.length >= 6) {
      try {
        const res = await axios.get(`${API}/festivals/by-code/${eventCode.toUpperCase()}`);
        setFestivalInfo(res.data);
      } catch {
        setFestivalInfo(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventCode || !judgeCode) {
      toast.error("Please enter both codes");
      return;
    }

    setLoading(true);
    // Retry up to 5 times if backend is sleeping (network error)
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/judge-login`, {
          event_code: eventCode.toUpperCase(),
          judge_code: judgeCode.toUpperCase()
        });

        safeStorage.setItem("judgeSession", JSON.stringify(response.data));
        toast.success(`Welcome, ${response.data.name}!`);
        navigate(`/judge-scoring/${response.data.judge_id}`);
        return;
      } catch (error) {
        if (!error.response && attempt < 4) {
          // Network error = backend sleeping, wait and retry
          toast.info("Connecting to server...");
          await new Promise(r => setTimeout(r, 3000));
          continue;
        }
        const message = error.response?.data?.detail || "Login failed. Please try again.";
        toast.error(message);
        break;
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFD700]/10 rounded-2xl mb-4">
            <Gavel className="w-8 h-8 text-[#FFD700]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
            JackAndJill
          </h1>
          <p className="text-[#A1A1AA]">Judge Login</p>
        </div>

        {/* Login Form */}
        <div className="glass p-8 rounded-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Code */}
            <div>
              <Label className="text-white mb-2 block flex items-center gap-2">
                <Hash className="w-4 h-4 text-[#FFD700]" />
                Event Code
              </Label>
              <Input
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                onBlur={handleEventCodeBlur}
                placeholder="e.g. ATH202625"
                className="h-14 text-xl text-center tracking-widest bg-black/30 border-white/10 text-white placeholder:text-[#52525B] font-mono"
                maxLength={12}
                required
                data-testid="event-code-input"
              />
              {festivalInfo && (
                <motion.p 
                  className="text-[#00E5FF] text-sm mt-2 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {festivalInfo.name}
                </motion.p>
              )}
            </div>

            {/* Judge Code */}
            <div>
              <Label className="text-white mb-2 block flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-[#FF007F]" />
                Judge Code
              </Label>
              <Input
                value={judgeCode}
                onChange={(e) => setJudgeCode(e.target.value.toUpperCase())}
                placeholder="e.g. JUDGE-1234"
                className="h-14 text-xl text-center tracking-widest bg-black/30 border-white/10 text-white placeholder:text-[#52525B] font-mono"
                maxLength={12}
                required
                data-testid="judge-code-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-gold h-14 text-lg font-bold"
              disabled={loading || !eventCode || !judgeCode}
              data-testid="judge-login-btn"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  Enter Scoring
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-[#52525B] text-sm">
              Don't have a code? Contact your event organizer.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[#52525B] text-xs mt-8">
          JackAndJill.com - Dance Competition Platform
        </p>
      </motion.div>
    </div>
  );
}
